import { z } from "zod";
import type {
  Commentary,
  CreateCommentaryRequest,
  UpdateCommentaryRequest,
} from "../../../api/commentaries.service.js";

export const commentaryFormSchema = z.object({
  locationRefId: z.string().optional(),
  bpaRefId: z.string().min(1, "Practice is required"),
  publicationRefId: z.string().min(1, "Publication is required"),
  editionRefId: z.string().min(1, "Edition is required"),
  assignee: z.string().min(1, "Assignee Email is required"),
  rankingTrend: z.string().optional(),
  summary: z.string().optional(),
  legalMarketConditions: z.string().optional(),
  externalMarketConditions: z.string().optional(),
  legislativePoliticalTrends: z.string().optional(),
  crossPracticeOverview: z.string().optional(),
  recommendedComparators: z.string().optional(),
  rankingDecisionMatrixDifferentiators: z.string().optional(),
  rankingDecisionMatrixChallenges: z.string().optional(),
  market: z.string().optional(),
  peerGroup: z.string().optional(),
  capabilityDifferentiators: z.string().optional(),
  capabilityChallenges: z.string().optional(),
  quotesPromoter: z.string().optional(),
  quotesPassives: z.string().optional(),
  quotesDetractors: z.string().optional(),
  headlineDifferentiators: z.array(z.string()).max(4).optional(),
  headlineChallenges: z.array(z.string()).max(4).optional(),
  kpis: z.array(z.string()).max(4).optional(),
  clientPrioritiesHigh: z.array(z.string()).max(10).optional(),
  clientPrioritiesMedium: z.array(z.string()).max(10).optional(),
  clientPrioritiesLow: z.array(z.string()).max(10).optional(),
  meetingClientNeedsHigh: z.array(z.string()).max(10).optional(),
  meetingClientNeedsMedium: z.array(z.string()).max(10).optional(),
  meetingClientNeedsLow: z.array(z.string()).max(10).optional(),
  strategicRecommendations: z.array(z.string()).max(4).optional(),
});

export const commentaryEditFormSchema = commentaryFormSchema.extend({
  locationRefId: z.string().optional(),
  bpaRefId: z.string().optional(),
  publicationRefId: z.string().optional(),
  editionRefId: z.string().optional(),
  assignee: z.string().optional(),
});

export type CommentaryFormValues = z.infer<typeof commentaryFormSchema>;

export type OptionValue = string | number | null;

export type SelectOption = {
  value: OptionValue;
  label: string;
};

export type TaxonomyLabelMaps = {
  publicationById: Map<string, string>;
  editionById: Map<string, string>;
  locationById: Map<string, string>;
  businessPracticeAreaById: Map<string, string>;
};

export type TaxonomySelectionState = {
  firmId: string | null;
  firmName: string | null;
  publicationName?: string | null;
  editionName?: string | null;
  locationName?: string | null;
  practiceName?: string | null;
};

export type CommentaryFormInitialState = {
  values: CommentaryFormValues;
  selectionState: TaxonomySelectionState;
};

export const commentaryTextFields = [
  "rankingTrend",
  "summary",
  "legalMarketConditions",
  "externalMarketConditions",
  "legislativePoliticalTrends",
  "crossPracticeOverview",
  "recommendedComparators",
  "rankingDecisionMatrixDifferentiators",
  "rankingDecisionMatrixChallenges",
  "market",
  "peerGroup",
  "capabilityDifferentiators",
  "capabilityChallenges",
  "quotesPromoter",
  "quotesPassives",
  "quotesDetractors",
] as const;

export const commentaryArrayFields = [
  "headlineDifferentiators",
  "headlineChallenges",
  "kpis",
  "clientPrioritiesHigh",
  "clientPrioritiesMedium",
  "clientPrioritiesLow",
  "meetingClientNeedsHigh",
  "meetingClientNeedsMedium",
  "meetingClientNeedsLow",
  "strategicRecommendations",
] as const;

export const commentaryFormDefaultValues: CommentaryFormValues = {
  locationRefId: "",
  bpaRefId: "",
  publicationRefId: "",
  editionRefId: "",
  assignee: "",
  rankingTrend: "",
  summary: "",
  legalMarketConditions: "",
  externalMarketConditions: "",
  legislativePoliticalTrends: "",
  crossPracticeOverview: "",
  recommendedComparators: "",
  rankingDecisionMatrixDifferentiators: "",
  rankingDecisionMatrixChallenges: "",
  market: "",
  peerGroup: "",
  capabilityDifferentiators: "",
  capabilityChallenges: "",
  quotesPromoter: "",
  quotesPassives: "",
  quotesDetractors: "",
  headlineDifferentiators: [],
  headlineChallenges: [],
  kpis: [],
  clientPrioritiesHigh: [],
  clientPrioritiesMedium: [],
  clientPrioritiesLow: [],
  meetingClientNeedsHigh: [],
  meetingClientNeedsMedium: [],
  meetingClientNeedsLow: [],
  strategicRecommendations: [],
};

function normalizeRefId(refId: string | null | undefined): string | undefined {
  if (!refId) return undefined;
  const normalized = refId.trim();
  return normalized.length > 0 ? normalized : undefined;
}

export function toOptionMap(options: SelectOption[]): Map<string, string> {
  const entries = options
    .map(option => {
      if (option.value === null) return null;
      const normalizedValue = normalizeRefId(String(option.value));
      if (!normalizedValue) return null;
      return [normalizedValue, option.label] as const;
    })
    .filter(entry => entry !== null);

  return new Map(entries);
}

function filterEmptyStrings(arr: string[] | undefined): string[] | undefined {
  if (!arr) return undefined;
  const filtered = arr.filter(s => s.trim() !== "");
  return filtered.length > 0 ? filtered : undefined;
}

function filterEmptyStringsToArray(arr: string[] | undefined): string[] {
  if (!arr) return [];
  return arr.filter(s => s.trim() !== "");
}

function toNullableText(value: string | undefined): string | null {
  if (!value || value.trim().length === 0) {
    return null;
  }

  return value;
}

export function buildCreateCommentaryRequest(
  data: CommentaryFormValues,
  selectionState: TaxonomySelectionState,
  labelMaps: TaxonomyLabelMaps,
): CreateCommentaryRequest {
  const publicationId = normalizeRefId(data.publicationRefId);
  const editionId = normalizeRefId(data.editionRefId);
  const locationId = normalizeRefId(data.locationRefId);
  const bpaId = normalizeRefId(data.bpaRefId);
  const firmId = normalizeRefId(selectionState.firmId);

  return {
    publicationId: publicationId ?? "",
    editionId: editionId ?? "",
    bpaId: bpaId ?? "",
    firmId: firmId ?? "",
    ...(locationId ? { locationId } : {}),
    ...(publicationId && labelMaps.publicationById.get(publicationId)
      ? { publicationName: labelMaps.publicationById.get(publicationId) }
      : {}),
    ...(editionId && labelMaps.editionById.get(editionId)
      ? { editionName: labelMaps.editionById.get(editionId) }
      : {}),
    ...(locationId && labelMaps.locationById.get(locationId)
      ? { locationName: labelMaps.locationById.get(locationId) }
      : {}),
    ...(bpaId && labelMaps.businessPracticeAreaById.get(bpaId)
      ? { bpaName: labelMaps.businessPracticeAreaById.get(bpaId) }
      : {}),
    ...(selectionState.firmName ? { firmName: selectionState.firmName } : {}),
    ...(data.assignee ? { assignee: data.assignee } : {}),
    ...(data.rankingTrend ? { rankingTrend: data.rankingTrend } : {}),
    ...(data.summary ? { summary: data.summary } : {}),
    ...(data.legalMarketConditions ? { legalMarketConditions: data.legalMarketConditions } : {}),
    ...(data.externalMarketConditions
      ? { externalMarketConditions: data.externalMarketConditions }
      : {}),
    ...(data.legislativePoliticalTrends
      ? { legislativePoliticalTrends: data.legislativePoliticalTrends }
      : {}),
    ...(data.crossPracticeOverview ? { crossPracticeOverview: data.crossPracticeOverview } : {}),
    ...(data.recommendedComparators ? { recommendedComparators: data.recommendedComparators } : {}),
    ...(data.rankingDecisionMatrixDifferentiators
      ? { rankingDecisionMatrixDifferentiators: data.rankingDecisionMatrixDifferentiators }
      : {}),
    ...(data.rankingDecisionMatrixChallenges
      ? { rankingDecisionMatrixChallenges: data.rankingDecisionMatrixChallenges }
      : {}),
    ...(data.market ? { market: data.market } : {}),
    ...(data.peerGroup ? { peerGroup: data.peerGroup } : {}),
    ...(data.capabilityDifferentiators
      ? { capabilityDifferentiators: data.capabilityDifferentiators }
      : {}),
    ...(data.capabilityChallenges ? { capabilityChallenges: data.capabilityChallenges } : {}),
    ...(data.quotesPromoter ? { quotesPromoter: data.quotesPromoter } : {}),
    ...(data.quotesPassives ? { quotesPassives: data.quotesPassives } : {}),
    ...(data.quotesDetractors ? { quotesDetractors: data.quotesDetractors } : {}),
    headlineDifferentiators: filterEmptyStrings(data.headlineDifferentiators),
    headlineChallenges: filterEmptyStrings(data.headlineChallenges),
    kpis: filterEmptyStrings(data.kpis),
    clientPrioritiesHigh: filterEmptyStrings(data.clientPrioritiesHigh),
    clientPrioritiesMedium: filterEmptyStrings(data.clientPrioritiesMedium),
    clientPrioritiesLow: filterEmptyStrings(data.clientPrioritiesLow),
    meetingClientNeedsHigh: filterEmptyStrings(data.meetingClientNeedsHigh),
    meetingClientNeedsMedium: filterEmptyStrings(data.meetingClientNeedsMedium),
    meetingClientNeedsLow: filterEmptyStrings(data.meetingClientNeedsLow),
    strategicRecommendations: filterEmptyStrings(data.strategicRecommendations),
  };
}

export function buildUpdateCommentaryRequest(data: CommentaryFormValues): UpdateCommentaryRequest {
  const request: UpdateCommentaryRequest = {};

  for (const field of commentaryTextFields) {
    request[field] = toNullableText(data[field]);
  }

  for (const field of commentaryArrayFields) {
    request[field] = filterEmptyStringsToArray(data[field]);
  }

  return request;
}

export function mapCommentaryToFormInitialState(
  commentary: Commentary,
): CommentaryFormInitialState {
  const content = commentary.content;

  return {
    values: {
      locationRefId: commentary.locationId ?? "",
      bpaRefId: commentary.bpaId ?? "",
      publicationRefId: commentary.publicationId ?? "",
      editionRefId: commentary.editionId ?? "",
      assignee: commentary.assignee ?? "",
      rankingTrend: content?.rankingTrend ?? "",
      summary: content?.summary ?? "",
      legalMarketConditions: content?.legalMarketConditions ?? "",
      externalMarketConditions: content?.externalMarketConditions ?? "",
      legislativePoliticalTrends: content?.legislativePoliticalTrends ?? "",
      crossPracticeOverview: content?.crossPracticeOverview ?? "",
      recommendedComparators: content?.recommendedComparators ?? "",
      rankingDecisionMatrixDifferentiators: content?.rankingDecisionMatrixDifferentiators ?? "",
      rankingDecisionMatrixChallenges: content?.rankingDecisionMatrixChallenges ?? "",
      market: content?.market ?? "",
      peerGroup: content?.peerGroup ?? "",
      capabilityDifferentiators: content?.capabilityDifferentiators ?? "",
      capabilityChallenges: content?.capabilityChallenges ?? "",
      quotesPromoter: content?.quotesPromoter ?? "",
      quotesPassives: content?.quotesPassives ?? "",
      quotesDetractors: content?.quotesDetractors ?? "",
      headlineDifferentiators: content?.headlineDifferentiators ?? [],
      headlineChallenges: content?.headlineChallenges ?? [],
      kpis: content?.kpis ?? [],
      clientPrioritiesHigh: content?.clientPrioritiesHigh ?? [],
      clientPrioritiesMedium: content?.clientPrioritiesMedium ?? [],
      clientPrioritiesLow: content?.clientPrioritiesLow ?? [],
      meetingClientNeedsHigh: content?.meetingClientNeedsHigh ?? [],
      meetingClientNeedsMedium: content?.meetingClientNeedsMedium ?? [],
      meetingClientNeedsLow: content?.meetingClientNeedsLow ?? [],
      strategicRecommendations: content?.strategicRecommendations ?? [],
    },
    selectionState: {
      firmId: commentary.firmId,
      firmName: commentary.firmName,
      publicationName: commentary.publicationName,
      editionName: commentary.editionName,
      locationName: commentary.locationName,
      practiceName: commentary.bpaName,
    },
  };
}

export function stripDirectoryPrefix(
  editionName: string | null | undefined,
  publicationName: string | null | undefined,
): string {
  if (!editionName) return "";
  if (!publicationName) return editionName.trim();

  const trimmedEdition = editionName.trim();
  const trimmedPublication = publicationName.trim();

  if (trimmedEdition.startsWith(trimmedPublication)) {
    return trimmedEdition.slice(trimmedPublication.length).trim();
  }

  return trimmedEdition;
}
